import { useRef, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';

/**
 * Debounces execution of a function specified in callback with specified delay.
 * The function is called on the trailing edge of the delay, so execution will be always delayed.
 *
 * @param callback
 * @param delay
 * @returns
 *
 * @example
 *
 * const debouncedCallback = useDebounce<ParamsType>((params) => {
 *  // do something with params
 * }, 1000);
 *
 * useEffect(() => {
 *   // effect is called everytime when dep1 or dep2 changes
 *   // but debouncedCallback is called only every 1000ms
 *   // with the latest arguments
 *   debouncedCallback({
 *     param1: dep1,
 *     param2: dep2
 *   })
 * })
 * }, [dep1, dep2])
 *
 * Also you might want to cancel the debounced trailing call, in order to do that you can use the following code:
 *
 * const debouncedCallback = useDebounce<ParamsType>((params) => {
 *  // do something with params
 * }, 1000);
 *
 * useEffect(() => {
 *   // cancel previous debounce
 *   debouncedCallback.cancel();
 *
 *   debouncedCallback({
 *     param1: dep1,
 *     param2: dep2
 *   })
 *
 *   return () => {
 *     // cancel debounce on component unmount
 *     debouncedCallback.cancel();
 *   }
 * })
 * }, [dep1, dep2])
 *
 */

export function useDebounce<T = undefined>(callback: (params: T) => void, delay: number) {
  const ref = useRef<(params: T) => void>();

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  const debouncedCallback = useMemo(() => {
    const func = (params: T) => {
      ref.current?.(params);
    };

    return debounce(func, delay, {
      leading: false,
      trailing: true,
    });
  }, [delay]);

  return debouncedCallback;
}
